/*!
 * viz.js — viz(): the look of what a uid draws
 * © 2026 Rob Canning — GPLv3
 *
 * Whatever the live console made carries a uid: a sound's box (the scope of
 * synth s3, the waveform of audio a1) or a made shape (sh1). viz() reaches
 * it by that name, to put it somewhere else or make it look different:
 *
 *     viz(s3, size:1)                     fill the screen
 *     viz(s3, size:[1, 0.3], at:[0.5, 0.2])
 *     viz(s3, stroke:#ff2a6d, width:3)    the trace: colour and line width
 *     viz(s3, fill:#000, border:#0ff, opacity:0.6)
 *     viz(sh1, fill:#f60, at:[0.7, 0.4], size:0.2)
 *     viz(orion, stroke:#ff2a6d, size:0.8)  a drawing use() put up: lines, insides, box
 *     viz(s3, view:polar)                 wave, polar, phase, xy, spectrum,
 *                                         ridge, waterfall
 *     viz(s3, view:ridge, rows:40, span:4)  the last 4 beats as 40 stacked lines
 *     viz(s3, view:phase, lag:20)         phase: y is the signal lag samples later
 *     viz(s3, view:phase, cycle:0.25)     ... or a fraction of its own cycle (a sine:
 *                                         0.25 a circle at any pitch); either can
 *                                         follow(): lag:follow(tri.sin, 5, 200)
 *     viz(s3, trail:2)                    traces leave tails fading over 2 beats (0 = none)
 *     viz(s3, spin:20)                    each voice's trace turns round the box once in 20 beats;
 *     viz(s3, spin:[20, -20])             ... voice by voice: these the other way round
 *     viz(s3, spin:birand(10, 30))        ... each voice its own speed and way
 *     viz(s3, palette:pastel)             each voice the next colour: live, grey,
 *                                         primary, pastel, neon
 *     viz(choir, trail:2)                 a shape's (a cluster's) movements leave tails
 *     viz(sh1, fill:scope, view:polar)    its sound drawn inside it (shapeLive.js)
 *     viz(s3, blend:difference)           mixed with what is under it (vizStyle blendOf)
 *     viz(s3, blur:6)                     soft: this one alone (screen(blur:) is the stage)
 *     viz(s3, kaleido:6, hue:120, warp:20) any of screen()'s effects, on this one (boxFx.js)
 *     viz(s3, blur:follow(s3.level, 12, 0))  sharp when loud, a smudge when quiet
 *     viz(s3, cell:4)                     in cell 4 of the grid (grid(3, 2), stageGrid.js);
 *     viz(s3, cell:free, trig:every(4, beat.hit))  ... an empty one, another every bar
 *
 * size: and at: are fractions of the screen -- the band between the top bar
 * and the transport: size:1 fills it, [w, h] gives width and height apart
 * (a shape takes one number, of the band's shorter side), and at: is the
 * centre, 0..1 across and down (at:0.5 is [0.5, 0.5]). A size without at: keeps the centre but
 * nudges the box back onto the screen. Applied, both become score units:
 * the box stays at that place in the score as it scrolls.
 *
 * stroke: and width: are the line that is drawn -- a scope's trace, a
 * waveform's, a shape's outline (color: is another name for stroke:);
 * fill: is the box's background and border: its outline.
 */

import { stageBand, atPair } from "../system/viewBand.js";
import { placeLiveRect, syncAudioViews } from "./audio/audioFile.js";
import { waveformsForElement } from "../system/waveform.js";
import { applyVizLine, PALETTES, paletteOf, definePalette, blendOf } from "../system/vizStyle.js";
import { VIEWS } from "../system/scopeViews.js";
import { handleShapeCue, watchDrags } from "./shape.js";
import { handleUseLive } from "./useLive.js";
import { evalMaybeRandom, makeValueGenerator } from "./audio/audioShared.js";
import { placeText, lookText, textFxKey, textFxTargets, wearTextFx, setTextTrail } from "./text.js";
import { setShapeTrail, SHAPE_VIEWS } from "./shapeLive.js";
import { isMoving, bindParam, releaseBinding } from "../control/paramBinding.js";
import { secs, secPerBeat } from "./beats.js";
import { isRel, applyRel } from "./relative.js";
import { FX_KEYS, setBoxFx, clearBoxFx, boxFxValue, boxFxKeys } from "../system/boxFx.js";
import { cellOf, cellPlacement, remember, forget, placedIn } from "../system/stageGrid.js";
import { isPending } from "./audio/liveAnalysers.js";
import { maskFromArgs } from "../system/stageMask.js";
import { onReset } from "../system/teardown.js";
import { mapHtml, rebend, forgetHtml } from "../system/mapHandles.js";
import { mapText } from "../system/mapWarp.js";

/**
 * width:*2, opacity:*0.5, size:*1.5, span:*2 ... -- each from what the box
 * or shape it reaches has now (relative.js). Returns the AST with plain values.
 */
function relativeToDrawn(found, ast) {
  if (!ast.args.some(a => isRel(a.value))) return ast;
  const el = found.el, d = el?.dataset || {};
  const body = el?.querySelector?.("path.oscilla-shape-body");
  const now = {
    width: found.kind === "box" ? Number(d.vizWidth) || 1 : Number(body?.getAttribute("stroke-width")) || 2,
    opacity: Number(el?.getAttribute?.("opacity") ?? 1),
    size: found.kind === "shape" && d.size != null && !String(d.size).startsWith("[") ? Number(d.size) : undefined,
    rows: Number(d.vizRows) || 24,
    span: d.vizSpan != null ? Number(d.vizSpan) / secPerBeat() : 2,
    trail: d.vizTrail != null ? Number(d.vizTrail) / secPerBeat() : 0,
    lag: Number(d.vizLag) || 12,
    cycle: d.vizCycle != null ? Number(d.vizCycle) : 0.25,
  };
  // the effects (boxFx.js): from what it has, else as plain (1 for the ones that scale)
  for (const k of FX_KEYS) now[k] = Number(boxFxValue(el, k) ?? (["contrast", "saturate", "bright"].includes(k) ? 1 : 0)) || 0;
  return { ...ast, args: ast.args.map(a => (isRel(a.value) ? { ...a, value: applyRel(now[a.type], a.value) } : a)) };
}

const num = (v, fallback) => {
  const r = Number(evalMaybeRandom(v));
  return Number.isFinite(r) ? r : fallback;
};

/** The box or shape a uid draws: synth boxes are scope-<uid>. */
function findDrawn(uid) {
  const byId = document.getElementById(uid);
  if (byId?.classList?.contains("oscilla-block")) return { kind: "block", el: byId };
  if (byId?.classList?.contains("oscilla-shape")) return { kind: "shape", el: byId };
  if (byId?.classList?.contains("oscilla-shape-cluster")) return { kind: "cluster", el: byId };
  if (byId?.dataset?.liveAudio != null) return { kind: "box", el: byId };
  const scope = document.getElementById(`scope-${uid}`);
  if (scope?.dataset?.liveAudio != null) return { kind: "box", el: scope };
  // a picture (image.js): what its frames show, in groups of their own
  const held = document.querySelector(`.oscilla-image-holder[data-image-uid="${CSS.escape(uid)}"]`);
  if (held) return { kind: "image", el: held };
  if (window.activeCueTexts?.has(uid) || document.querySelector(`.cue-text-overlay[data-uid="${CSS.escape(uid)}"]`)) {
    return { kind: "text", el: textFxKey(uid), uid };
  }
  return null;
}

/** The uncovered band of the screen, in px: { left, top, width, height }. */
const screenBand = stageBand;

/**
 * The phase view's lag on what is drawn (a box, or each member of a shape
 * cluster): lag: in samples or cycle: a fraction of the sound's cycle --
 * whichever is set last -- each a number or follow(...), which keeps
 * writing it as the signal moves. A number set later lets the follow go.
 */
function setPhaseLag(owner, targets, arg) {
  const lag = arg("lag"), cycle = arg("cycle");
  const [key, value, fallback] = cycle != null ? ["vizCycle", cycle, 0.25] : ["vizLag", lag, 12];
  if (value == null) return;
  const other = key === "vizCycle" ? "vizLag" : "vizCycle";
  const write = v => { for (const t of targets) { t.dataset[key] = String(v); delete t.dataset[other]; } };
  const ownerKey = `viz-lag:${owner.id}`;
  if (isMoving(value)) bindParam(value, write, { owner: ownerKey, default: fallback });
  else { releaseBinding(ownerKey); write(num(value, fallback)); }
}

// ---------------------------------------------------------------
//  Sound boxes
// ---------------------------------------------------------------

/** [w, h] of the screen band from size: (one number is both). */
function sizeFraction(size) {
  const [w, h] = Array.isArray(size) ? size : [size, size];
  return [num(w, 1), num(h ?? w, 1)];
}

/**
 * The frame a box is placed in: its group in the score, above any wrapper
 * an animation put round it -- through a turning wrapper, an upright screen
 * rectangle would come out turned (or flat).
 */
function frameOf(rect) {
  let n = rect.parentNode;
  while (n && [...(n.classList || [])].some(c => c.startsWith("oscilla-anim"))) n = n.parentNode;
  return n;
}

/** unset: -- a box's settings, by the name a viz() line gives them, and where each is kept. */
const BOX_SETTING = {
  view: "vizView", trail: "vizTrail", rows: "vizRows", span: "vizSpan", step: "vizStep", tween: "vizTween",
  scroll: "vizScroll", width: "vizWidth", color: "vizStroke", stroke: "vizStroke", palette: "vizPalette",
  y: "vizY", spin: "vizSpin", blend: "vizBlend", lag: "vizLag", cycle: "vizCycle", glyphs: "vizGlyphs",
  drag: "drag",
};

/**
 * viz(s, unset:[scroll, step]): those settings back to their defaults -- what
 * the console sends when a line run again no longer says them (liveEval.js
 * resetsFor). What follows a signal lets go; the line style is laid again.
 */
function unsetBox(rect, keys) {
  const list = (Array.isArray(keys) ? keys : [keys]).map(String);
  for (const k of list) if (BOX_SETTING[k]) delete rect.dataset[BOX_SETTING[k]];
  if (list.includes("width")) releaseBinding(`viz-width:${rect.id}`);
  if (list.includes("lag") || list.includes("cycle")) releaseBinding(`viz-lag:${rect.id}`);
  if (list.includes("palette")) rect._paletteNext = 0;
  const groups = waveformsForElement(rect).map(h => h.group).filter(Boolean);
  if (list.includes("blend")) { rect.style.mixBlendMode = ""; for (const g of groups) g.style.mixBlendMode = ""; }
  if (list.includes("opacity")) {
    releaseBinding(`viz-opacity:${rect.id}`);
    rect.removeAttribute("opacity");
    for (const g of groups) g.removeAttribute("opacity");
  }
  if (list.includes("view") && !rect.id.startsWith("scope-")) syncAudioViews(rect);
  for (const h of waveformsForElement(rect)) for (const l of [h._scopeLine, h.upperLine, h.lowerLine]) if (l) applyVizLine(l, rect);
}

function vizBox(rect, arg) {
  const unset = arg("unset");
  if (unset != null) unsetBox(rect, unset);
  // drag:1 -- picked up anywhere on what it draws, its scope over it (shape.js pressOnStage)
  const drag = arg("drag");
  if (drag != null) {
    if (["0", "off", "false", "none"].includes(String(drag))) delete rect.dataset.drag;
    else { rect.dataset.drag = "1"; watchDrags(); }
  }
  const at = arg("at"), size = arg("size");
  if (at != null || size != null) placeBox(rect, at, size);
  vizStyle(rect, arg);
  // map:1 / map:[...] / map:0 -- its drawing bent onto a surface (boxMap)
  const map = arg("map");
  if (map != null) boxMap(rect, map);
  else if (unset != null && [].concat(unset).map(String).includes("map")) boxMap(rect, 0);
}

// ---- map: -- a box's drawing bent onto a surface (mapHandles.js) -------------------
//
// A console box draws on canvases in foreignObjects (scopeViews.js
// boxCanvas), reaching past the box: each is bent so the box's part of it
// lands on the four points. Those canvases move and grow as the sound does,
// so each frame a mapped box is looked at, and bent again when they have.

const bentBoxes = new Map();            // uid -> { rect, sig }
let bentRaf = 0;

/** The canvases a box draws on now, each with the box's part of it, in its own px. */
function boxTargets(rect) {
  // the box's group carries the box's translate (waveform.js): the box and its canvases in the same units
  const [x, y, w, h] = ["x", "y", "width", "height"].map(k => Number(rect.getAttribute(k)));
  const out = [];
  for (const hd of waveformsForElement(rect)) {
    for (const rec of [hd._trail, hd._ridgeCv, hd._geom]) {
      if (!rec?.canvas || !rec.fo?.isConnected) continue;
      const fx = Number(rec.fo.getAttribute("x")), fy = Number(rec.fo.getAttribute("y"));
      out.push({ el: rec.canvas, part: [x - fx, y - fy, w, h] });
    }
  }
  return out;
}

const boxSig = rect => boxTargets(rect).map(({ el, part }) => `${part.join(",")}:${el.offsetWidth}x${el.offsetHeight}`).join("|")
  + `|${rect.getAttribute("transform") || ""}`;

function watchBent() {
  if (bentRaf) return;
  const tick = () => {
    bentRaf = 0;
    for (const [uid, b] of bentBoxes) {
      if (!b.rect.isConnected) { forgetHtml(`viz:${uid}`); bentBoxes.delete(uid); continue; }
      const sig = boxSig(b.rect);
      if (sig !== b.sig) { b.sig = sig; rebend(`viz:${uid}`); }
    }
    if (bentBoxes.size) bentRaf = requestAnimationFrame(tick);
  };
  bentRaf = requestAnimationFrame(tick);
}

function boxMap(rect, v) {
  const uid = rect.id.replace(/^scope-/, "");
  const on = mapHtml(`viz:${uid}`, v, {
    flat: () => rect.getBoundingClientRect(),
    targets: () => boxTargets(rect),
    line: pts => `viz(${uid}, map:${mapText(pts)})`,
    write: { uid, cues: ["viz"] },
  });
  if (on) { bentBoxes.set(uid, { rect, sig: "" }); watchBent(); }
  else bentBoxes.delete(uid);
}

/**
 * at:/size: -- the box's middle and size as shares of the stage (size: may
 * be left out: the size it has). Kept on the box (rect._place) as shares,
 * so a resized window puts it back in the same place (and size) again.
 */
function placeBox(rect, at, size) {
  {
    const ctm = frameOf(rect)?.getScreenCTM();
    if (!ctm) return;
    const inv = ctm.inverse();
    const toScreen = (x, y) => new DOMPoint(x, y).matrixTransform(ctm);
    const band = screenBand();
    // the box now, in screen px, as if upright (any drag translate included)
    const t = (rect.getAttribute("transform") || "").match(/translate\(\s*([-\d.e]+)[\s,]+([-\d.e]+)/);
    const bx = Number(rect.getAttribute("x")) + (t ? Number(t[1]) : 0);
    const by = Number(rect.getAttribute("y")) + (t ? Number(t[2]) : 0);
    const a = toScreen(bx, by);
    const b = toScreen(bx + Number(rect.getAttribute("width")), by + Number(rect.getAttribute("height")));
    let w = b.x - a.x, h = b.y - a.y;
    if (size != null) {
      const [fw, fh] = sizeFraction(size);
      w = fw * band.width; h = fh * band.height;
    }
    let cx = (a.x + b.x) / 2, cy = (a.y + b.y) / 2;
    if (Array.isArray(at)) {
      cx = band.left + num(at[0], 0.5) * band.width;
      cy = band.top + num(at[1], 0.5) * band.height;
    } else {
      // resized where it is: keep it on the screen
      const fit = (c, half, lo, len) => len <= 2 * half ? lo + len / 2 : Math.min(lo + len - half, Math.max(lo + half, c));
      cx = fit(cx, w / 2, band.left, band.width);
      cy = fit(cy, h / 2, band.top, band.height);
    }
    const p0 = new DOMPoint(cx - w / 2, cy - h / 2).matrixTransform(inv);
    const p1 = new DOMPoint(cx + w / 2, cy + h / 2).matrixTransform(inv);
    placeLiveRect(rect, { x: p0.x, y: p0.y, w: p1.x - p0.x, h: p1.y - p0.y });
    rect._place = {
      at: [(cx - band.left) / (band.width || 1), (cy - band.top) / (band.height || 1)],
      size: size ?? rect._place?.size ?? null,
    };
  }
}

// a resized window: every box placed by viz() (or by hand) where it was, as
// shares -- on Oscilla Live's stage, the window's own; in a score a box keeps
// its place in the score, and scrolls with it
let _replaceTimer = null;
if (typeof window !== "undefined") {
  window.addEventListener("resize", () => {
    clearTimeout(_replaceTimer);
    _replaceTimer = setTimeout(() => {
      if (!document.querySelector("#scrollStage[data-live-stage]")) return;
      for (const rect of document.querySelectorAll("rect[data-live-audio]")) {
        if (rect._place && !rect._autoPlaced) placeBox(rect, rect._place.at, rect._place.size);
      }
    }, 150);
  });
}

function vizStyle(rect, arg) {
  // The line's style is kept on the box: scopes come and go in it (each
  // run of a poly synth draws a new voice), and each one drawn takes it
  // (vizStyle.js). Then it goes onto what is drawn now.
  const handles = waveformsForElement(rect);
  const stroke = arg("stroke") ?? arg("color"), width = arg("width"), palette = arg("palette");
  if (palette != null) {
    // a palette's name, or a list of colours of its own: palette:[red, #05d9e8]
    const p = Array.isArray(palette) ? JSON.stringify(palette.map(String)) : String(palette);
    if (paletteOf(p)) {
      rect.dataset.vizPalette = p;
      delete rect.dataset.vizStroke;         // a palette is asked for: one colour no longer
      rect._paletteNext = 0;
      for (const h of handles) for (const l of [h._scopeLine, h.upperLine, h.lowerLine]) delete l?.dataset.palette;
    } else console.warn(`[viz] palette:${p} — one of ${Object.keys(PALETTES).join(", ")}, a list (palette:[red, #05d9e8]), or one named with palette(mine, [...])`);
  }
  if (stroke != null) rect.dataset.vizStroke = String(stroke);
  // width:follow(bass.low, 0.5, 40) -- the line as thick as the signal says
  // (each frame's trace reads it from the box)
  const widthOwner = `viz-width:${rect.id}`;
  if (width != null && isMoving(width)) {
    bindParam(width, w => {
      rect.dataset.vizWidth = String(w);
      for (const h of waveformsForElement(rect)) for (const l of [h._scopeLine, h.upperLine, h.lowerLine]) applyVizLine(l, rect);
    }, { owner: widthOwner, min: 0.05, max: 200, default: 1 });
  } else if (width != null) {
    releaseBinding(widthOwner);
    rect.dataset.vizWidth = String(num(width, 1));
  }
  if (stroke != null || width != null || palette != null) {
    for (const h of handles) {
      for (const l of [h._scopeLine, h.upperLine, h.lowerLine]) applyVizLine(l, rect);
      const c = h._scopeLine?.getAttribute("stroke");
      if (c) h.cursor?.setAttribute("stroke", c);
    }
  }
  // How the sound is drawn -- kept on the box like the line's style; every
  // scope in it reads it each frame (scope.js, scopeViews.js)
  const view = arg("view");
  if (view != null) {
    const v = String(view);
    if (VIEWS.has(v)) rect.dataset.vizView = v;
    else console.warn(`[viz] view:${v} — one of ${[...VIEWS].join(", ")}`);
  }
  setPhaseLag(rect, [rect], arg);
  // view:xy -- the sound drawn up the box (its uid)
  const y = arg("y");
  if (y != null) rect.dataset.vizY = String(y);
  // spin: beats each voice's trace takes to turn round the box's middle
  // (- the other way): [20, -20] voice by voice, birand(10, 30) a draw each
  // (scope.js voiceSpin); 0 none
  const spin = arg("spin");
  if (spin != null) {
    if (/^(0|off|none)$/i.test(String(spin))) delete rect.dataset.vizSpin;
    else rect.dataset.vizSpin = JSON.stringify(spin);
  }
  // trail: beats a trace takes to fade (0 = none); kept in seconds, at most 30
  const trail = arg("trail");
  if (trail != null) rect.dataset.vizTrail = String(Math.min(30, Math.max(0, num(secs(trail), 0))));
  // view:ridge / waterfall -- rows: lines over span: beats (kept in seconds)
  const rows = arg("rows"), span = arg("span");
  // (a ridge keeps up to 120 lines; a barcode's columns go to 512 -- each view clamps its own)
  if (rows != null) rect.dataset.vizRows = String(Math.max(2, Math.min(512, Math.round(num(rows, 24)))));
  // step: beats each barcode holds, on the beat grid (0: a new one every frame)
  const step = arg("step");
  if (step != null) rect.dataset.vizStep = String(Math.max(0, num(step, 0)));
  // scroll: a barcode as a conveyor -- right / left / up / down; off (or 0) still again
  const scroll = arg("scroll");
  if (scroll != null) {
    const w = String(scroll).toLowerCase();
    if (["right", "left", "up", "down"].includes(w)) rect.dataset.vizScroll = w;
    else delete rect.dataset.vizScroll;
  }
  // glyphs: the trace in characters -- a word along it, or hex / bin / dec digits anew each frame
  const glyphs = arg("glyphs");
  if (glyphs != null) {
    const w = String(glyphs).replace(/^["']|["']$/g, "");
    if (/^(off|none|0)$/i.test(w) || !w.trim()) delete rect.dataset.vizGlyphs;
    else rect.dataset.vizGlyphs = w;
  }
  // tween: the share of each step a barcode's change takes, eased (0: at once)
  const tween = arg("tween");
  if (tween != null) rect.dataset.vizTween = String(Math.max(0, Math.min(1, num(tween, 0))));
  if (span != null) rect.dataset.vizSpan = String(Math.max(0.05, num(secs(span), 2)));
  // an audio box draws the file until asked otherwise: its voices need
  // live scopes of their own (audioFile.js)
  if (view != null && !rect.id.startsWith("scope-")) syncAudioViews(rect);

  const fill = arg("fill"), border = arg("border"), opacity = arg("opacity");
  if (fill != null) {
    rect.setAttribute("fill", String(fill));
    // fill:none -- asked for: a ridge's lines see-through too (scopeRidge.js)
    if (["none", "transparent", "0"].includes(String(fill))) rect.dataset.vizFill = "none";
    else delete rect.dataset.vizFill;
  }
  // blend: -- the box and what is drawn in it, mixed with the boxes under it;
  // kept on the box for the voices still to come (scope.js renderScope)
  const blend = arg("blend");
  if (blend != null) {
    const b = blendOf(blend);
    if (b != null) {
      rect.dataset.vizBlend = b;
      rect.style.mixBlendMode = b;
      for (const h of waveformsForElement(rect)) if (h.group) h.group.style.mixBlendMode = b;
    }
  }
  if (border != null) {
    rect.setAttribute("stroke", String(border));
    rect.setAttribute("stroke-width", "1.5");                 // the faint default is 0.5
    rect.setAttribute("vector-effect", "non-scaling-stroke");
  }
  if (opacity != null) {
    // a number, or follow(...): opacity:follow(noise.fader) -- the box and
    // everything drawn in it fade with the sound
    const write = v => {
      const o = String(Math.min(1, Math.max(0, Number(v))));
      rect.setAttribute("opacity", o);
      for (const h of waveformsForElement(rect)) h.group?.setAttribute("opacity", o);
    };
    const owner = `viz-opacity:${rect.id}`;
    if (isMoving(opacity)) bindParam(opacity, write, { owner, min: 0, max: 1, default: 1 });
    else { releaseBinding(owner); write(num(opacity, 1)); }
  }
}

// ---------------------------------------------------------------
//  Shapes: their own cue does the work
// ---------------------------------------------------------------

/**
 * What only viz() sets on a shape or a cluster: trail: (one for a cluster,
 * under all its members), and view:, lag:, cycle: for a fill:scope (each member's).
 */
function vizShapeDrawing(el, arg) {
  const trail = arg("trail");
  if (trail != null) setShapeTrail(el, Math.min(30, Math.max(0, num(secs(trail), 0))));   // beats -> seconds
  const view = arg("view");
  if (view != null && !SHAPE_VIEWS.has(String(view))) {
    console.warn(`[viz] view:${view} on a shape — one of ${[...SHAPE_VIEWS].join(", ")}`);
  }
  const members = el.classList.contains("oscilla-shape-cluster") ? el.querySelectorAll(".oscilla-shape") : [el];
  for (const m of members) {
    if (view != null && SHAPE_VIEWS.has(String(view))) m.dataset.vizView = String(view);
  }
  setPhaseLag(el, [...members], arg);
}

function vizShape(el, ast, arg) {
  const pass = ast.args.filter(a => ["fill", "stroke", "width", "opacity", "at", "blend", "drag", "map", "_inCell", "unset"].includes(a.type));
  const color = arg("color");
  if (color != null && arg("stroke") == null) pass.push({ type: "stroke", value: color });
  const size = arg("size");                       // shape() reads it the same way
  if (size != null) pass.push({ type: "size", value: size });
  handleShapeCue({ type: "cueShape", args: [{ type: "uid", value: el.id }, ...pass, { type: "_fromViz", value: true }] });
}

// ---------------------------------------------------------------
//  The grid: cell: as at: and size:
// ---------------------------------------------------------------

/** The line with its cell: made into at: and size:. */
function placedArgs(ast, kind, entry) {
  const p = cellPlacement(kind, entry, screenBand());
  const rest = ast.args.filter(a => !["cell", "across", "down", "at", "size"].includes(a.type));
  // _inCell: the grid's own at:, which does not take it out of the grid (shape.js)
  rest.push({ type: "at", value: p.at }, { type: "_inCell", value: true });
  if (p.size != null) rest.push({ type: "size", value: p.size });
  return { ...ast, args: rest };
}

/**
 * cell: on a viz() line: which cell (stageGrid.js cellOf) and what it asks
 * inside it, remembered for a new grid, a shuffle or a turn; null when
 * there is no grid or no such cell (said why).
 */
function cellEntry(uid, arg) {
  let v = arg("cell");
  if (v && typeof v === "object" && v.type === "funcCall") v = evalMaybeRandom(v);   // choose(1, 3) in a score
  const { cell, why } = cellOf(v, uid, { makeGen: makeValueGenerator });
  if (cell == null) { console.warn(`[viz] ${why}`); return null; }
  const entry = { cell, across: num(arg("across"), 1), down: num(arg("down"), 1), size: arg("size"), at: arg("at") };
  // the kept one: a viz() waiting for its box puts it where a shuffle has put it since
  return remember(uid, entry, arg("cell"));
}

/**
 * palette(mine, [red, green, blue]): a palette by that name, for
 * viz(s3, palette:mine) -- or a name the palettes have (primary) with other
 * colours. A box already drawing in it keeps its voices' colours; the next
 * voices take the new ones.
 */
export function handlePaletteCue(ast) {
  const name = String(ast?.args?.find(a => a.type === "uid")?.value ?? "").trim();
  const colours = ast?.args?.find(a => a.type === "names")?.value;
  if (!definePalette(name, colours)) console.warn("[palette] a name and its colours: palette(mine, [red, #05d9e8, blue])");
}

/** The canvases a box or a picture (the camera's) paints on -- what a mask: clips. */
function drawnCanvases(found) {
  if (found.kind === "image") return [...(found.el?.querySelectorAll?.("canvas") || [])];
  const out = [];
  for (const h of waveformsForElement(found.el)) {
    for (const c of [h._trail?.canvas, h._ridgeCv?.canvas, h._stageTrail?.canvas, h._geom?.canvas]) if (c) out.push(c);
    for (const c of h.group?.querySelectorAll?.("canvas") || []) if (!out.includes(c)) out.push(c);
  }
  return out;
}

/** Everything the grid holds, put in its cell again (a new grid, a shuffle, a turn). */
export function placeGridded() {
  for (const { uid, ...entry } of placedIn()) {
    const found = findDrawn(uid);
    if (!found) { forget(uid); continue; }
    handleVizCue({ type: "cueViz", args: [{ type: "uid", value: uid }] }, { entry, found });
  }
}

/** Where it is and how it looks, by what it is -- all but its effects (vizFx). */
function placeDrawn(found, ast, arg) {
  if (found.kind === "shape") { vizShape(found.el, ast, arg); vizShapeDrawing(found.el, arg); }
  // a drawing from blocks/: its own cue, with no file named, changes the look alone
  else if (found.kind === "block") {
    const look = ast.args.filter(a => ["fill", "stroke", "color", "width", "opacity", "at", "size"].includes(a.type));
    handleUseLive({ type: "cueUse", args: [{ type: "uid", value: found.el.id }, ...look] });
  }
  else if (found.kind === "text") vizText(String(arg("uid")), arg);
  else if (found.kind === "cluster") {
    // every member's colour, line and opacity; where each is and how big
    // stays its own (its own roll)
    const look = { ...ast, args: ast.args.filter(a => ["uid", "fill", "stroke", "color", "width", "opacity", "blend"].includes(a.type)) };
    const lookArg = name => look.args.find(a => a.type === name)?.value;
    for (const m of found.el.querySelectorAll(".oscilla-shape")) vizShape(m, look, lookArg);
    vizShapeDrawing(found.el, arg);
  }
  else if (found.kind === "image") {
    // a picture: its effects (vizFx); the camera's moved by the grid (cam.js)
    if (found.el?.dataset?.cam != null && arg("_inCell") != null) import("./cam.js").then(m => m.moveCam(found.el.dataset.imageUid, arg("at"), arg("size")));
  }
  else vizBox(found.el, arg);
}

const WAIT_MS = 5000, WAIT_STEP_MS = 200;
const PENDING_MS = 60000;           // ... the external input, while the browser asks for it (adc.js)
const waiting = new Map();          // uid -> timer of a viz() waiting for its box

/**
 * Every viz() still waiting for its box, let go: a cleared stage (a new
 * example) must not have the last one's viz(bass, ...) land on its own bass.
 */
export function cancelVizWaits() {
  for (const t of waiting.values()) clearInterval(t);
  waiting.clear();
}
onReset("stage", cancelVizWaits);

/**
 * A text on the screen (text() with no target): at:[x, y] puts it there,
 * from now on (every letter of a shuffle, every run of the line); fill: or
 * color: is its colour, opacity: how solid, trail: beats its words take to
 * fade behind them (text.js setTextTrail).
 */
function vizText(uid, arg) {
  const at = arg("at");
  if (Array.isArray(at)) placeText(uid, num(at[0], 0.5), num(at[1], 0.5));
  const color = arg("fill") ?? arg("color") ?? arg("stroke");
  const opacity = arg("opacity");
  const look = {};
  if (color != null) look.color = String(color);
  if (opacity != null) look.opacity = Math.min(1, Math.max(0, num(opacity, 1)));
  const blend = arg("blend");
  if (blend != null && blendOf(blend) != null) look.blend = blendOf(blend);
  if (Object.keys(look).length) lookText(uid, look);
  // trail: beats its words take to fade behind them (0 = none); a line run again without it: none
  const trail = arg("trail");
  if (trail != null) setTextTrail(uid, Math.min(30, Math.max(0, num(secs(trail), 0))));
  else if ([].concat(arg("unset") ?? []).map(String).includes("trail")) setTextTrail(uid, 0);
}

/**
 * screen()'s effects on what one uid draws (boxFx.js): blur:, hue:, invert:,
 * contrast:, saturate:, bright:, pixel:, warp: (grain:), split: -- and on a
 * sound's box kaleido: (turn:), mirror:, tile:. A number or follow(...);
 * 0 takes one off, fx:off all. A box: each voice's trace, a trail on the
 * stage, voices to come; a shape, a cluster, a drawing: the whole of it; a
 * text: its words, now and to come (viz(voices, blur:) a blur that stays --
 * text(blur:) is how each word arrives).
 */
function vizFx(found, args, tweak = false) {
  const el = found.el, kind = found.kind === "box" ? "box" : "shape";
  const textUid = found.kind === "text" ? found.uid : null;
  const wear = textUid != null ? f => wearTextFx(textUid, f) : null;
  const imageUid = found.kind === "image" ? el.dataset.imageUid : null;
  const targets = kind === "box"
    ? () => waveformsForElement(el).flatMap(h => [h.group, ...(h._detached || [])]).filter(Boolean)
    // a picture: every frame of its uid, and a fullscreen one
    : imageUid != null
      ? () => [...document.querySelectorAll(`.oscilla-image-holder[data-image-uid="${CSS.escape(imageUid)}"], .oscilla-image-fullscreen[data-image-uid="${CSS.escape(imageUid)}"]`)]
      : textUid != null ? () => textFxTargets(textUid) : () => [el];
  if (args.some(a => a.type === "fx" && /^(off|0|none)$/i.test(String(a.value)))) {
    for (const k of FX_KEYS) releaseBinding(`viz-fx:${el.id}:${k}`);
    clearBoxFx(el);
  }
  // The line is the box's effects: one it no longer names goes (kaleido:3
  // taken out and the line run again -- plain). Not after a tweak of its
  // own (warp:*2: the others stay).
  if (!tweak) {
    const named = new Set(args.map(a => a.type));
    for (const k of boxFxKeys(el)) {
      if (named.has(k)) continue;
      releaseBinding(`viz-fx:${el.id}:${k}`);
      setBoxFx(el, kind, k, null, targets, wear);
    }
  }
  for (const a of args) {
    if (!FX_KEYS.includes(a.type)) continue;
    const owner = `viz-fx:${el.id}:${a.type}`;
    const write = v => setBoxFx(el, kind, a.type, v, targets, wear);
    if (isMoving(a.value)) bindParam(a.value, write, { owner, min: -1e6, max: 1e6, default: 0 });
    else {
      releaseBinding(owner);
      const v = evalMaybeRandom(a.value);
      write(/^(0|off|none)$/i.test(String(v)) ? null : v);
    }
  }
}

/**
 * viz(uid, ...). `place` (the grid's, placeGridded): put it in that cell
 * again and change nothing else -- a viz() line is the box's effects, and
 * this one has none.
 */
export function handleVizCue(ast, place = null) {
  // at:0.5 is at:[0.5, 0.5]
  const arg = name => { const v = ast?.args?.find(a => a.type === name)?.value; return name === "at" ? atPair(v) : v; };
  const uid = String(arg("uid") ?? "").trim();
  if (!uid) { console.warn("[viz] which one? viz(s3, …) or viz(uid:s3, …)"); return; }
  if (place) {
    ast = placedArgs(ast, place.found.kind, place.entry);
    placeDrawn(place.found, ast, arg);
    return;
  }
  // cell: -- the grid's (stageGrid.js), as at: and size: when it is drawn;
  // at: alone takes it out of the grid
  const entry = arg("cell") != null ? cellEntry(uid, arg) : null;
  if (arg("cell") == null && arg("at") != null) forget(uid);
  // effects all given relative (warp:*2): a tweak, the box's other effects stay
  const fxGiven = ast.args.filter(a => FX_KEYS.includes(a.type));
  const fxTweak = fxGiven.length > 0 && fxGiven.every(a => isRel(a.value));
  const apply = (found) => {
    if (entry) {
      if (found.kind === "cluster" || found.kind === "image") console.warn(`[viz] cell: places a box, a shape, a drawing or a text -- not ${found.kind === "image" ? "a picture: image(..., cell:4)" : "a cluster: shape(n:6, cell:free) gives each member one"}`);
      else ast = placedArgs(ast, found.kind, entry);
    }
    ast = relativeToDrawn(found, ast);
    placeDrawn(found, ast, arg);
    // mask:spot / hole:spot -- a box's drawing (its canvases), or a picture's, through a shape
    if (found.kind === "box" || found.kind === "image") maskFromArgs(`viz:${uid}`, () => drawnCanvases(found), arg);
    vizFx(found, ast.args, fxTweak);
  };
  clearInterval(waiting.get(uid));
  waiting.delete(uid);
  const found = findDrawn(uid);
  if (found) { apply(found); return; }
  // Not there yet: a sound's box appears when it starts, and a file can
  // take a while to load -- audio(...) then viz(...) straight after is
  // the natural thing to type. Wait for it, then give up with a word.
  const started = performance.now();
  waiting.set(uid, setInterval(() => {
    const now = findDrawn(uid);
    const waited = performance.now() - started;
    if (!now && (waited < WAIT_MS || (isPending(uid) && waited < PENDING_MS))) return;
    clearInterval(waiting.get(uid));
    waiting.delete(uid);
    if (now) apply(now);
    else console.warn(`[viz] nothing drawn by ${uid} — a sound with a box (once it plays), or a made shape`);
  }, WAIT_STEP_MS));
}
